import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { datadogRum } from '@datadog/browser-rum';
import {
  newAppointment as newAppointmentAction,
  resetAppointment as resetAppointmentAction,
} from 'actions';
import {
  clearProfessional as clearProfessionalAction,
  selectProfessional as selectProfessionalAction,
} from 'actions/SelectedProfessionalActions';
import { trackEvent } from 'integrations/metrics';
import { EventNames } from 'integrations/metrics/events';
import { EventAction, EventObjectType } from 'integrations/metrics/types';
import { ServiceSelection } from 'reducers/selectedServices';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { getBookableAppointments } from 'selectors/bookableAppointments';
import {
  getAllProfessionals,
  getSelectedProfessional,
} from 'selectors/professionals';

import { useAppContext } from 'hooks/context/useAppContext';
import { usePreferences } from 'hooks/preferences/usePreferences';
import { useServices } from 'hooks/services/useServices';

import { AutoBookingErrorModal } from 'routes/book/AutoBookingErrorModal';
import { BookingHeader } from 'routes/services/BookingHeader';
import { MaxAppointmentsWarning } from 'routes/services/MaxAppointmentsWarning/MaxAppointmentsWarning';
import { OfflineBookingModal } from 'routes/services/OfflineBookingModal/OfflineBookingModal.component';
import { ServiceOptionsModal } from 'routes/services/ServiceOptionsModal';
import { ServiceProviderChangeModal } from 'routes/services/ServiceProviderChangeModal/ServiceProviderChangeModal.component';
import { ServiceSelectors } from 'routes/services/ServiceSelectors/ServiceSelectors.component';

import { TProfessional } from 'types/appointment';
import { TService, TServiceOption } from 'types/service';

export type TServiceProps = {
  onClick: (guid: string) => void;
  listView?: boolean;
  selected?: boolean;
  selectedOption?: TServiceOption;
  disabled?: boolean;
  service: TService;
  serviceProvider?: string;
};

export type TServiceOptionsModalProps = {
  service?: TService;
  onClose: () => void;
  onSelect: (
    serviceID: string,
    optionID: string,
    bookingOrder?: number,
  ) => void;
};

const filterServices = (
  services: TService[] = [],
  selectedServiceCategory: string,
) => {
  if (!selectedServiceCategory || selectedServiceCategory === 'View All') {
    return services;
  }

  return services.filter(
    (item) => item.category_name === selectedServiceCategory,
  );
};

const ANY_PROFESSIONAL = 'Any Professional';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useServicesPage = () => {
  // Router hooks
  const router = useRouter();
  const rwgToken = router.query?.rwg_token;

  // Context Hooks
  const { publicUser } = useAppContext();
  const { multi_user: isMultiUser } = publicUser;
  const { isListView, isOnlineBookingDisabled } = usePreferences();

  // Global State
  const professionals = useSelector(getAllProfessionals);
  const selectedProfessional = useSelector(getSelectedProfessional);
  const bookableAppointments = useSelector(getBookableAppointments);
  const selectedServicesState = useSelector(
    (state: any) => state.selectedServices || [],
  ) as ServiceSelection[];

  const {
    services,
    selectedServices,
    serviceCategories,
    selectedServiceCategory,
    showMaxAppointmentsWarning,
    toggleService,
    selectServiceCategory,
    clearServices,
    clearServiceCategory,
    getSelectedOption,
    onCloseMaxAppointmentsWarning,
  } = useServices();

  // Local state
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [localSelectedProfessional, setLocalSelectedProfessional] =
    React.useState<TProfessional | null>(null);
  const [showingOfflineBookingModal, setShowingOfflineBookingModal] =
    React.useState(false);
  const [pendingSelectService, setPendingSelectService] =
    React.useState<TService>();

  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  // Dispatch handlers
  const selectProfessional = (professional: TProfessional | null) =>
    dispatch(selectProfessionalAction(professional));
  const clearProfessional = () => dispatch(clearProfessionalAction());

  const resetAppointment = () => dispatch(resetAppointmentAction());

  const onSelectProfessional = (selectedProfessionalGuid: string) => {
    const professional =
      professionals.find((item) => item.guid === selectedProfessionalGuid) ||
      null;

    selectProfessional(professional);
    selectServiceCategory(null);

    trackEvent(EventNames.CLIENT_SELECTED_PROFESSIONAL, {
      user_id: professional?.guid || '',
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setLocalSelectedProfessional(null);
    document.body.style.overflow = '';
  };

  const handleModalOKClick = () => {
    clearServices();
    setIsModalOpen(false);
    selectProfessional(localSelectedProfessional);
    setLocalSelectedProfessional(null);
    selectServiceCategory(null);
    document.body.style.overflow = '';
  };

  const showOfflineBookingModal = () => {
    setShowingOfflineBookingModal(true);
  };

  const selectServiceOrOpenServiceOptionsModal =
    (service: TService) => (id: string) => {
      const selected = selectedServices
        ? selectedServices?.includes(id)
        : false;

      if (Boolean(service.options?.length) && !selected) {
        setPendingSelectService(service);

        trackEvent(EventNames.SERVICE_OPTIONS_VIEWED, {
          service_options_count: service.options?.length,
        });
      } else {
        toggleService(id, undefined, service.booking_order);
      }
    };

  const selectServiceOption = (
    id: string,
    optionId: string,
    bookingOrder?: number,
  ) => {
    toggleService(id, optionId, bookingOrder);
  };

  const closeServiceOptionModal = () => {
    setPendingSelectService(undefined);
  };

  const filteredServices = filterServices(services, selectedServiceCategory);

  let headingText;

  if (isMultiUser) {
    headingText = 'Filter your booking';
  } else if (serviceCategories && serviceCategories.length > 0) {
    headingText = 'Choose one of my specialties';
  }

  const handleBookingClick = () => {
    if (isOnlineBookingDisabled) {
      showOfflineBookingModal();
    } else {
      let isMSMP = false;

      datadogRum.addAction('booking_started');

      if (bookableAppointments?.length > 1) {
        void router.push('/multi-book');
        isMSMP = true;
      } else {
        dispatch(
          newAppointmentAction({
            rwg_token: rwgToken,
            selectedServices: selectedServices ?? [],
            professional: selectedProfessional,
          }),
        );

        void router.push('/book');
      }

      trackEvent(EventNames.BOOK_BUTTON_CLICKED, {
        action: EventAction.SELECT,
        productSection: 'Booking',
        productSubSection: 'Services',
        objectType: EventObjectType.BUTTON,
        objectValue: 'Book',
        is_msmp_booking: isMSMP,
        service_ids: selectedServices ?? [],
      });
    }
  };

  const getServiceProvider = (service: TService) => {
    if (!professionals) return ANY_PROFESSIONAL;

    const { guid } = service;

    // Check if this service has been selected
    const selectedService = selectedServicesState.find(
      (s: ServiceSelection) => s.guid === guid,
    );

    if (selectedService) {
      // If service was selected with a specific provider, show that provider
      if (selectedService.providerGuid) {
        const selectedProvider = professionals.find(
          (p) => p.guid === selectedService.providerGuid,
        );

        return selectedProvider?.full_name || ANY_PROFESSIONAL;
      }
      // If service was selected with "Any Provider", always show "Any Professional"
      return ANY_PROFESSIONAL;
    }

    // For unselected services, show the currently selected professional filter
    // or "Any Professional" if no specific professional is selected
    return selectedProfessional?.full_name || ANY_PROFESSIONAL;
  };

  const handleClearClick = () => {
    clearProfessional();
    clearServices();
    clearServiceCategory();
    resetAppointment();
    void router.push('/booking-flow');
  };

  const renderBookingHeader = () => {
    return (
      <BookingHeader onBook={handleBookingClick} onClear={handleClearClick} />
    );
  };

  const renderSelectors = () => {
    return (
      <ServiceSelectors
        isMultiUser={isMultiUser}
        serviceCategories={serviceCategories}
        selectedServiceCategory={selectedServiceCategory}
        selectServiceCategory={selectServiceCategory}
        selectedProfessional={selectedProfessional}
        onSelectProfessional={onSelectProfessional}
        professionals={professionals}
      />
    );
  };

  const renderService = (
    service: TService,
    ServiceComponent: (props: TServiceProps) => JSX.Element,
  ) => (
    <ServiceComponent
      onClick={selectServiceOrOpenServiceOptionsModal(service)}
      listView={isListView}
      selected={
        selectedServices ? selectedServices?.includes(service.guid) : false
      }
      service={service}
      selectedOption={getSelectedOption(service)}
      serviceProvider={getServiceProvider(service)}
    />
  );

  const renderOfflineBookingModal = () => (
    <OfflineBookingModal
      isOpen={showingOfflineBookingModal}
      onClose={() => setShowingOfflineBookingModal(false)}
    />
  );

  const renderMaxAppointmentsWarning = () => (
    <MaxAppointmentsWarning
      isOpen={showMaxAppointmentsWarning}
      onClose={onCloseMaxAppointmentsWarning}
    />
  );

  const renderServiceProviderChangeModal = () => (
    <ServiceProviderChangeModal
      isOpen={isModalOpen}
      onClose={closeModal}
      onOKClick={handleModalOKClick}
    />
  );

  const renderServiceOptionsModal = () => (
    <ServiceOptionsModal
      service={pendingSelectService}
      onClose={closeServiceOptionModal}
      onSelect={selectServiceOption}
    />
  );

  const renderAutoBookingErrorModal = () => {
    return <AutoBookingErrorModal />;
  };

  return {
    headingText,
    renderSelectors,
    isListView,
    isMultiUser,
    selectedServices,
    resetAppointment,
    professionals,
    onSelectProfessional,
    selectedProfessional,
    serviceCategories,
    selectedServiceCategory,
    selectServiceCategory,
    showOfflineBookingModal,
    showMaxAppointmentsWarning,
    isModalOpen,
    renderBookingHeader,
    renderOfflineBookingModal,
    renderAutoBookingErrorModal,
    renderServiceProviderChangeModal,
    renderServiceOptionsModal,
    renderMaxAppointmentsWarning,
    services: filteredServices,
    renderService,
  };
};
